"use client";

import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ButtonIcon } from "@/components/button/buttonIcon/buttonIcon";
import styles from "./MinimalDialog.module.css";

/* ------------------------------------------------------------------
   Context

   ONE dialog is rendered per group, by the provider — not one per card.
   Cards only register their content and render a trigger. That is what lets
   next/prev swap the content while the dialog shell, its close button and
   the arrows stay mounted; a dialog per card unmounts and remounts on every
   step, which reads as close-then-reopen.

   Context rather than props because roughly half the `CardContentItem` call
   sites in CardSection.tsx — and 3 of 4 `CardsGrid` call sites, including
   the plain grid — never thread extra props down.
   ------------------------------------------------------------------ */

interface DialogEntry {
  media: React.ReactNode;
  children: React.ReactNode;
}

interface MinimalDialogContextValue {
  register: (id: string) => () => void;
  setEntry: (id: string, entry: DialogEntry) => void;
  setActive: (id: string | null) => void;
  activeId: string | null;
}

const MinimalDialogContext = React.createContext<MinimalDialogContextValue | null>(null);

export function MinimalDialogProvider({ children }: { children: React.ReactNode }) {
  // Order and content live in refs so a card re-rendering doesn't churn
  // provider state; only `activeId` and `count` drive renders.
  const orderRef = React.useRef<string[]>([]);
  const entriesRef = React.useRef<Map<string, DialogEntry>>(new Map());
  const [count, setCount] = React.useState(0);
  const [activeId, setActive] = React.useState<string | null>(null);

  const register = React.useCallback((id: string) => {
    if (!orderRef.current.includes(id)) {
      orderRef.current = [...orderRef.current, id];
      setCount(orderRef.current.length);
    }
    return () => {
      orderRef.current = orderRef.current.filter((x) => x !== id);
      entriesRef.current.delete(id);
      setCount(orderRef.current.length);
      setActive((current) => (current === id ? null : current));
    };
  }, []);

  const setEntry = React.useCallback((id: string, entry: DialogEntry) => {
    entriesRef.current.set(id, entry);
  }, []);

  const go = React.useCallback((delta: number) => {
    setActive((current) => {
      const ids = orderRef.current;
      if (!current || ids.length === 0) return current;
      const i = ids.indexOf(current);
      if (i === -1) return current;
      return ids[(i + delta + ids.length) % ids.length];
    });
  }, []);

  const value = React.useMemo<MinimalDialogContextValue>(
    () => ({ register, setEntry, setActive, activeId }),
    [register, setEntry, activeId],
  );

  // Keep showing the last card while the dialog animates out. Dropping to
  // `undefined` the moment activeId clears would empty the media and caption
  // mid-exit, collapsing the box to bare padding as it fades.
  const lastIdRef = React.useRef<string | null>(null);
  if (activeId) lastIdRef.current = activeId;
  const displayId = activeId ?? lastIdRef.current;
  const entry = displayId ? entriesRef.current.get(displayId) : undefined;

  return (
    <MinimalDialogContext.Provider value={value}>
      {children}
      <MinimalDialogHost
        entry={entry}
        activeId={activeId}
        displayId={displayId}
        canNavigate={count > 1}
        onClose={() => setActive(null)}
        onPrev={() => go(-1)}
        onNext={() => go(1)}
      />
    </MinimalDialogContext.Provider>
  );
}

/* ------------------------------------------------------------------
   Host — the single, persistent dialog
   ------------------------------------------------------------------ */

function MinimalDialogHost({
  entry,
  activeId,
  displayId,
  canNavigate,
  onClose,
  onPrev,
  onNext,
}: {
  entry: DialogEntry | undefined;
  activeId: string | null;
  /** activeId, or the last one while closing — keys off this so the content
      isn't remounted (and emptied) by the close itself. */
  displayId: string | null;
  canNavigate: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <DialogPrimitive.Root open={!!activeId} onOpenChange={(next) => !next && onClose()}>
      <DialogPrimitive.Portal>
        {/* RemoveScroll is mounted on the Overlay, so this must render for
            the body scroll lock to apply. */}
        <DialogPrimitive.Overlay className={styles.overlay} />

        <DialogPrimitive.Content
          className={styles.content}
          /* The panel is arbitrary block-level rich text, and
             Dialog.Description renders a <p>, so nesting would be invalid
             HTML. Opt out; the dialog is still named by its Title. */
          aria-describedby={undefined}
        >
          {/* `key` remounts only these two on navigate, replaying the fade.
              The close button and the arrows sit outside, so they persist. */}
          <div key={`m-${displayId}`} className={`${styles.media} ${styles.swap}`}>
            {entry?.media}
          </div>

          <div className={styles.panel}>
            <div key={`p-${displayId}`} className={`${styles.panelContent} ${styles.swap}`}>
              {entry?.children}
            </div>

            {canNavigate ? (
              <div className={styles.panelNav}>
                <ButtonIcon
                  variant="white"
                  shape="circle"
                  size="md"
                  border
                  name="ArrowLeft"
                  aria-label="Previous"
                  onClick={onPrev}
                />
                <ButtonIcon
                  variant="white"
                  shape="circle"
                  size="md"
                  border
                  name="ArrowRight"
                  aria-label="Next"
                  onClick={onNext}
                />
              </div>
            ) : null}
          </div>

          <DialogPrimitive.Close asChild>
            <ButtonIcon
              variant="white"
              shape="circle"
              size="md"
              name="X"
              className={styles.close}
              aria-label="Close"
            />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* ------------------------------------------------------------------
   Per-card trigger

   Keeps the original API so `cardContent.tsx` is unchanged: it still passes
   `media` and the panel children; they're just routed to the shared host.
   ------------------------------------------------------------------ */

export interface MinimalDialogProps {
  /** Media node for the trigger (the card thumbnail). */
  media: React.ReactNode;
  /**
   * Media node for the dialog itself. Defaults to `media`. Kept separate so
   * the dialog can request a larger `sizes`/quality than the grid thumbnail
   * without the thumbnail over-fetching.
   */
  dialogMedia?: React.ReactNode;
  /** Panel contents (title, body), below the image. */
  children: React.ReactNode;
  /** Accessible name for the trigger button. */
  label: string;
  className?: string;
}

const MinimalDialog = ({ media, dialogMedia, children, label, className }: MinimalDialogProps) => {
  const ctx = React.useContext(MinimalDialogContext);
  const id = React.useId();

  // Depend on `register` (stable via useCallback), NOT on the context value —
  // that object changes whenever activeId does, and re-registering on every
  // open would clear activeId and close the dialog the instant it opened.
  const register = ctx?.register;
  React.useEffect(() => register?.(id), [register, id]);

  // Ref write rather than state: keeps the host's content current without a
  // card re-render cascading into the provider.
  ctx?.setEntry(id, { media: dialogMedia ?? media, children });

  // No provider (component used standalone): render the media unwrapped
  // rather than a trigger that can't open anything.
  if (!ctx) return <>{media}</>;

  return (
    <button
      type="button"
      className={[styles.trigger, className].filter(Boolean).join(" ")}
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={ctx.activeId === id}
      onClick={() => ctx.setActive(id)}
    >
      {media}
    </button>
  );
};

/* ------------------------------------------------------------------
   Panel parts
   ------------------------------------------------------------------ */

/**
 * Panel heading, rendered as the Radix Title via `asChild` so the dialog is
 * named by its in-body heading despite having no title bar.
 */
export const MinimalDialogTitle = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <DialogPrimitive.Title asChild>
    <h2 className={[styles.panelTitle, className].filter(Boolean).join(" ")}>{children}</h2>
  </DialogPrimitive.Title>
);

/** Visually hidden title, for cards with no heading at all. */
export const MinimalDialogHiddenTitle = ({ children }: { children: React.ReactNode }) => (
  <DialogPrimitive.Title
    style={{
      position: "absolute",
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: "hidden",
      clip: "rect(0,0,0,0)",
      whiteSpace: "nowrap",
      borderWidth: 0,
    }}
  >
    {children}
  </DialogPrimitive.Title>
);

export const MinimalDialogBody = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => <div className={[styles.panelBody, className].filter(Boolean).join(" ")}>{children}</div>;

export default MinimalDialog;
